import React from 'react'
import { ROLES } from '@config/roles'
import authService from '@services/authService'

// Dashboard imports - Role-specific dashboards
import SuperAdminDashboard from '@/pages/super-admin/Dashboard'
import CompanyDashboard from '@/pages/company/Dashboard'
import ComplianceManagerDashboard from '@/pages/company/Dashboard/ComplianceManagerDashboard'
import ProjectManagerDashboard from '@/pages/company/Dashboard/ProjectManagerDashboard'
import ExecutiveDashboard from '@/pages/company/Dashboard/ExecutiveDashboard'
import EmployeeDashboard from '@/pages/company/Dashboard/EmployeeDashboard'

/**
 * RoleBasedDashboard Component
 *
 * Dynamically renders the appropriate dashboard based on user role.
 * Each role gets a tailored dashboard experience:
 *
 * - SUPER_ADMIN: Platform-wide metrics, company management, system health
 * - COMPANY_ADMIN: Company overview, user management, compliance status
 * - COMPLIANCE_MANAGER: Compliance-focused metrics, framework progress, audit readiness
 * - PROJECT_MANAGER: Project oversight, approvals, team workload
 * - EXECUTIVE: Strategic view, pending decisions, risk summary
 * - EMPLOYEE: Personal tasks, deadlines, training progress
 *
 * This component acts as a router for the /dashboard path,
 * allowing all users to access the same URL while seeing role-appropriate content.
 */
const RoleBasedDashboard = () => {
	// Get current user from auth service
	const user = authService.getCurrentUser()

	// Map backend role slug to frontend ROLES constant
	// Backend sends role as an object with slug: 'super-admin', 'company-admin', etc.
	const getRoleFromUser = (userData) => {
		if (!userData || !userData.role) {
			return ROLES.EMPLOYEE
		}

		// Handle both role object (from backend) and role string
		const roleSlug = typeof userData.role === 'object'
			? userData.role.slug
			: userData.role

		// Map backend role slugs to frontend ROLES constants
		const roleMapping = {
			// Super Admin roles
			'super-admin': ROLES.SUPER_ADMIN,
			'superadmin': ROLES.SUPER_ADMIN,
			'product-owner': ROLES.SUPER_ADMIN,

			// Company Admin roles
			'company-admin': ROLES.COMPANY_ADMIN,

			// Compliance Manager roles
			'compliance-manager': ROLES.COMPLIANCE_MANAGER,

			// Project Manager roles
			'project-manager': ROLES.PROJECT_MANAGER,

			// Executive roles
			'executive': ROLES.EXECUTIVE,

			// Quality Manager roles (uses Employee dashboard with task focus)
			'quality-manager': ROLES.EMPLOYEE,
			'qa': ROLES.EMPLOYEE,

			// Employee roles (default)
			'employee': ROLES.EMPLOYEE,
			'team-member': ROLES.EMPLOYEE
		}

		return roleMapping[roleSlug?.toLowerCase()] || ROLES.EMPLOYEE
	}

	const userRole = getRoleFromUser(user)

	// Render appropriate dashboard based on role
	switch (userRole) {
		case ROLES.SUPER_ADMIN:
			return <SuperAdminDashboard />

		case ROLES.COMPANY_ADMIN:
			return <CompanyDashboard userRole={userRole} />

		case ROLES.COMPLIANCE_MANAGER:
			return <ComplianceManagerDashboard />

		case ROLES.PROJECT_MANAGER:
			return <ProjectManagerDashboard />

		case ROLES.EXECUTIVE:
			return <ExecutiveDashboard />

		case ROLES.EMPLOYEE:
		default:
			return <EmployeeDashboard />
	}
}

export default RoleBasedDashboard
